// Restore… and Restore to an instant… (spec/dashboard.md section 11.2; spec/api-conventions.md section 17.2): commands,
// never staged — `POST …/saves/{collection}:restore` with `toVersion` (the collection as of that version's time) or `at`,
// optionally some keys only, a required reason and force while the player is online. Restore to an instant for the whole
// player (section 11.2): one `:restore { at, reason, force }` per collection of the player, one after the other, one
// summary toast and one invalidation; a failure keeps the dialog open with the per-collection outcomes and Retry the failed.
import { useState } from 'react';
import { ops } from '../../api/operations';
import type { Player, RestoreResult, SaveVersion } from '../../api/types';
import { useAuth } from '../../auth/AuthContext';
import { invalidatePlayer, useCollection } from '../../players/usePlayer';
import { useGame } from '../../shell/GameContext';
import { Button } from '../../ui/Button';
import { Field } from '../../ui/Field';
import { Checkbox, Select, TextArea } from '../../ui/inputs';
import { Modal } from '../../ui/Modal';
import { REASON_HINT, reasonError } from '../../ui/ReasonDialog';
import { formatInstant, useTimeZone } from '../../ui/time';
import { useToasts } from '../../ui/Toast';
import { DateTimeInput } from '../../ui/values';
import { bumpText, commandErrorText, toastCommandError, wholeRestoreSummary, type RestoreOutcome } from './commands';
import { SkeletonRows } from '../../ui/PageStates';

/** The reason textarea of a command dialog: required, 2–2000 characters. */
export function ReasonField({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  const [touched, setTouched] = useState(false);
  const error = reasonError(value);
  return (
    <Field label="Reason" required help={REASON_HINT} error={touched && error ? error : undefined}>
      <TextArea aria-label="Reason" value={value} rows={3} onChange={(e) => onChange(e.target.value)} onBlur={() => setTouched(true)} />
    </Field>
  );
}

/** "Force (the player is online)", shown only while the player is online. */
export function ForceField({ player, value, onChange }: { player: Player; value: boolean; onChange: (v: boolean) => void }) {
  if (!player.online) return null;
  return (
    <div className="force-field">
      <Checkbox label="Force (the player is online)" checked={value} onChange={(e) => onChange(e.target.checked)} />
      <p className="muted small">The device's next save can overwrite a forced write.</p>
    </div>
  );
}

function restoredToast(r: RestoreResult): string {
  const keys = r.restored.map((k) => k.key);
  const bump = bumpText(r.bump);
  if (keys.length === 0) return `Nothing to restore in ${r.collection}: it already holds those values`;
  return `Restored ${keys.length} key(s) of ${r.collection}: ${keys.join(', ')}${bump ? ` · ${bump}` : ''}`;
}

function useRestore(env: string, player: Player, onDone: () => void) {
  const { client } = useAuth();
  const { org, game } = useGame();
  const toasts = useToasts();
  const [busy, setBusy] = useState(false);
  const run = async (collection: string, body: Record<string, unknown>) => {
    setBusy(true);
    try {
      const r = (await client.data(ops.restorePlayerSaves, { path: { org, game: game.slug, env, player: player.id, collection }, body: body })) as unknown as RestoreResult;
      toasts.success(restoredToast(r));
      invalidatePlayer(game.id, env, player.id);
      onDone();
    } catch (e) {
      toastCommandError(toasts, e);
    } finally {
      setBusy(false);
    }
  };
  return { busy, run };
}

export function RestoreVersionDialog({ env, player, version, onClose }: { env: string; player: Player; version: SaveVersion | null; onClose: () => void }) {
  return (
    <Modal title={version ? `Restore ${version.collection}` : 'Restore'} open={!!version} onClose={onClose} size="m">
      {version && <RestoreVersionBody env={env} player={player} version={version} onClose={onClose} />}
    </Modal>
  );
}

function RestoreVersionBody({ env, player, version, onClose }: { env: string; player: Player; version: SaveVersion; onClose: () => void }) {
  const { offsets } = useGame();
  const [zone] = useTimeZone();
  const collectionQ = useCollection(env, player.id, version.collection);
  const keys = (collectionQ.data?.keys ?? []).map((k) => k.key);
  const [whole, setWhole] = useState(true);
  const [picked, setPicked] = useState<Set<string>>(() => new Set([version.key]));
  const [reason, setReason] = useState('');
  const [force, setForce] = useState(false);
  const { busy, run } = useRestore(env, player, onClose);
  const choices = keys.includes(version.key) ? keys : [version.key, ...keys];
  const chosen = choices.filter((k) => picked.has(k));
  const valid = !reasonError(reason) && (whole || chosen.length > 0);
  const toggle = (k: string, on: boolean) =>
    setPicked((s) => {
      const next = new Set(s);
      if (on) next.add(k);
      else next.delete(k);
      return next;
    });
  const submit = () => {
    if (!valid) return;
    void run(version.collection, { toVersion: version.id, keys: whole ? undefined : chosen, reason: reason.trim(), force: player.online ? force : false });
  };
  return (
    <div data-testid="restore-dialog" className="command-dialog">
      <p>
        Restores <strong>{version.collection}</strong> as of {formatInstant(version.createdAt, zone, { offsets }, { seconds: true })} (the time of v{String(version.version)} of {version.key}). Keys that did not exist then are deleted; the history keeps
        everything, so a restore can itself be restored.
      </p>
      <Checkbox label="Whole collection" checked={whole} onChange={(e) => setWhole(e.target.checked)} />
      {!whole && (
        <fieldset className="restore-keys" aria-label="Keys to restore">
          <legend className="field-label">Keys</legend>
          {collectionQ.loading && !collectionQ.data && <SkeletonRows rows={2} label="Loading the keys" />}
          {choices.map((k) => (
            <Checkbox key={k} label={k} checked={picked.has(k)} onChange={(e) => toggle(k, e.target.checked)} />
          ))}
        </fieldset>
      )}
      <ReasonField value={reason} onChange={setReason} />
      <ForceField player={player} value={force} onChange={setForce} />
      <footer className="modal-footer form-footer command-actions">
        <Button variant="ghost" onClick={onClose}>
          Cancel
        </Button>
        <Button variant="primary" onClick={submit} disabled={!valid} busy={busy}>
          Restore
        </Button>
      </footer>
    </div>
  );
}

export function RestoreAtDialog({ env, player, open, collection, onClose }: { env: string; player: Player; open: boolean; collection?: string; onClose: () => void }) {
  return (
    <Modal title="Restore to an instant" open={open} onClose={onClose} size="m">
      {open && <RestoreAtBody env={env} player={player} initial={collection} onClose={onClose} />}
    </Modal>
  );
}

/** The Collection select's value for "Whole player (every collection)". */
const WHOLE = '*';

function RestoreAtBody({ env, player, initial, onClose }: { env: string; player: Player; initial?: string; onClose: () => void }) {
  const { client } = useAuth();
  const { org, game, offsets } = useGame();
  const toasts = useToasts();
  const names = player.collections.map((c) => c.collection);
  const [collection, setCollection] = useState(initial && names.includes(initial) ? initial : WHOLE);
  const [at, setAt] = useState<number | null>(null);
  const [reason, setReason] = useState('');
  const [force, setForce] = useState(false);
  const { busy, run } = useRestore(env, player, onClose);
  const [wholeBusy, setWholeBusy] = useState(false);
  const [outcomes, setOutcomes] = useState<RestoreOutcome[] | null>(null);
  const whole = collection === WHOLE;
  const valid = !!collection && at !== null && !reasonError(reason) && (!whole || names.length > 0);
  const failed = (outcomes ?? []).filter((o) => o.error !== undefined);

  /** One `:restore` per collection, one after the other; one invalidation at the end; the dialog stays open on a failure. */
  const restoreWhole = async (targets: string[]) => {
    const body = { at: new Date(at! * 1000).toISOString(), reason: reason.trim(), force: player.online ? force : false };
    setWholeBusy(true);
    const next = new Map((outcomes ?? []).map((o) => [o.collection, o]));
    for (const c of targets) {
      try {
        const r = (await client.data(ops.restorePlayerSaves, { path: { org, game: game.slug, env, player: player.id, collection: c }, body })) as unknown as RestoreResult;
        next.set(c, { collection: c, restored: r.restored.length, bump: bumpText(r.bump) || undefined });
      } catch (e) {
        next.set(c, { collection: c, restored: null, error: commandErrorText(e) });
      }
    }
    const all = names.filter((n) => next.has(n)).map((n) => next.get(n)!);
    invalidatePlayer(game.id, env, player.id);
    setWholeBusy(false);
    if (all.some((o) => o.error !== undefined)) {
      setOutcomes(all);
      return;
    }
    toasts.success(wholeRestoreSummary(all));
    onClose();
  };
  const submit = () => {
    if (!valid) return;
    if (whole) {
      void restoreWhole(names);
      return;
    }
    void run(collection, { at: new Date(at! * 1000).toISOString(), reason: reason.trim(), force: player.online ? force : false });
  };
  return (
    <div data-testid="restore-at-dialog" className="command-dialog">
      <p className="muted small">
        {whole
          ? 'Every key of every collection of the player gets its value as of that instant, one collection after the other: a key that did not exist then is deleted, a key deleted since is brought back.'
          : 'Every key of the collection gets its value as of that instant: a key that did not exist then is deleted, a key deleted since is brought back.'}
      </p>
      <Field label="Collection" required>
        <Select aria-label="Collection" value={collection} onChange={(e) => setCollection(e.target.value)} disabled={!!outcomes}>
          <option value={WHOLE}>Whole player (every collection)</option>
          {names.map((n) => (
            <option key={n} value={n}>
              {n}
            </option>
          ))}
        </Select>
      </Field>
      <Field label="Instant" required>
        <DateTimeInput aria-label="Instant" value={at} onCommit={setAt} ctx={{ offsets }} />
      </Field>
      <ReasonField value={reason} onChange={setReason} />
      <ForceField player={player} value={force} onChange={setForce} />
      {outcomes && (
        <ul className="restore-outcomes" aria-label="Restore outcomes" data-testid="restore-outcomes">
          {outcomes.map((o) => (
            <li key={o.collection} data-testid={`restore-outcome-${o.collection}`} className={o.error !== undefined ? 'failed' : 'done'}>
              <code>{o.collection}</code>{' '}
              {o.error !== undefined ? (
                <span className="restore-outcome-error">failed: {o.error}</span>
              ) : (
                <span className="muted">done{o.restored ? ` (${o.restored} key${o.restored === 1 ? '' : 's'})` : ', already held those values'}</span>
              )}
            </li>
          ))}
        </ul>
      )}
      <footer className="modal-footer form-footer command-actions">
        <Button variant="ghost" onClick={onClose}>
          {outcomes ? 'Close' : 'Cancel'}
        </Button>
        {outcomes && failed.length > 0 ? (
          <Button variant="primary" onClick={() => valid && void restoreWhole(failed.map((o) => o.collection))} disabled={!valid} busy={wholeBusy}>
            Retry the failed
          </Button>
        ) : (
          <Button variant="primary" onClick={submit} disabled={!valid} busy={busy || wholeBusy}>
            Restore
          </Button>
        )}
      </footer>
    </div>
  );
}
